import { HorizontalDotsChunky } from '@eventbrite/eds-iconography';
import { MoreActions } from '@eventbrite/eds-more-actions';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import React, { FunctionComponent, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { DiscoverState, EventSearch } from '../../../../types';
import styles from './PromotedActionsMenuEventCard.module.scss';
import {
    PromotedActions,
    sendActionMetrics,
    useRemoveEvent,
} from './PromotedActionsMenuEventCardUtils';
import { ReportAdModal, ReportAdModalProps } from './ReportAdModal';

export type PromotedActionsMenuEventCardProps = {
    event: FormattedEvent;
    actionStyle?: string;
} & Pick<ReportAdModalProps, 'getTrackingContext'>;

export type PromotedActionsMenuEventCardReduxProps = {
    eventSearch: EventSearch;
};

function PromotedActionsMenuEventCardInternal({
    event,
    eventSearch,
    getTrackingContext,
    actionStyle = 'dark',
}: PromotedActionsMenuEventCardProps & PromotedActionsMenuEventCardReduxProps) {
    const containerRef = useRef<HTMLSpanElement>(null);
    const { removeEvent } = useRemoveEvent(eventSearch);
    const [showReportModal, setReportModalVisibility] = useState(false);

    const actionHandler = (action: PromotedActions) => {
        switch (action) {
            case PromotedActions.hideAd:
                removeEvent(event.id);
                sendActionMetrics(event, PromotedActions.hideAd, containerRef);
                break;
            case PromotedActions.reportAd:
                setReportModalVisibility(true);
                break;
            default:
                break;
        }
    };

    return (
        <span className={styles.container} ref={containerRef}>
            <MoreActions
                items={[
                    {
                        content: gettext('Hide this ad'),
                        value: PromotedActions.hideAd,
                    },
                    {
                        content: gettext('Report this ad'),
                        value: PromotedActions.reportAd,
                    },
                ]}
                style={actionStyle}
                iconType={<HorizontalDotsChunky />}
                title={gettext('Promoted event actions')}
                onAction={actionHandler}
            />
            {showReportModal && (
                <ReportAdModal
                    event={event}
                    handleClose={() => setReportModalVisibility(false)}
                    // Due to the modal being contained inside the event card, if we remove the event card from the DOM in the onSubmit handler
                    // the modal will be unmounted, hence, the onSuccess modal never appears
                    // This was not done as an initial implementation due to the report modal re-using the event interaction tracker and requiring to fire a DOM event
                    // FIXME: Migrate this to a new endpoint and move modal outside of the event card to avoid this issue
                    onSuccessClose={() => removeEvent(event.id)}
                    getTrackingContext={getTrackingContext}
                />
            )}
        </span>
    );
}

const _mapStateToProps = (state: DiscoverState) => ({
    eventSearch: state.search.eventSearch,
});

export const PromotedActionsMenuEventCard: FunctionComponent<PromotedActionsMenuEventCardProps> =
    connect(_mapStateToProps)(PromotedActionsMenuEventCardInternal);
